import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Copy, Loader2, ShieldAlert, Trash2, Plus, Check, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Admin invite manager — generate shareable survey links, see their
// click/submission counts, and delete stale ones.
export default function AdminSurveyInvites() {
  const { user, login } = useAuth();
  const [state, setState] = useState({ loading: true, forbidden: false, items: [] });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ degree_id: "", label: "" });
  const [copiedTok, setCopiedTok] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get("/admin/survey/invites");
      setState({ loading: false, forbidden: false, items: r.data?.items || [] });
    } catch (err) {
      const status = err?.response?.status;
      setState({
        loading: false,
        forbidden: status === 401 || status === 403,
        items: [],
      });
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.degree_id.trim()) {
      toast.error("Enter the degree ID (e.g. uk-oxford__ppe__oxford__2027-10).");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/survey/invites", {
        degree_id: form.degree_id.trim(),
        label: form.label.trim() || null,
      });
      toast.success("Invite link created.");
      setForm({ degree_id: "", label: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite.");
    } finally {
      setCreating(false);
    }
  };

  const destroy = async (token) => {
    if (!window.confirm("Delete this invite link? Existing submissions stay, but no new ones will land under this token.")) return;
    try {
      await api.delete(`/admin/survey/invites/${token}`);
      toast.success("Invite deleted.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  const copy = async (token, degree_id) => {
    const url = `${window.location.origin}/survey/${degree_id}?t=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedTok(token);
      toast.success("Link copied.");
      setTimeout(() => setCopiedTok(null), 1500);
    } catch (_) {
      toast.error("Could not copy to clipboard.");
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm">
        Loading invites…
      </div>
    );
  }

  if (state.forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Admin access required</h1>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed">
            {user
              ? `Signed in as ${user.email}, but this account isn't on the admin list.`
              : "Please sign in with your admin Google account."}
          </p>
          {!user && (
            <Button onClick={login} className="mt-4 bg-[#4C1D95] hover:bg-[#3B0764] text-white">
              Sign in
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="admin-invites-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-stone-500">Admin</div>
          <h1 className="mt-1 font-editorial text-3xl">Survey invites</h1>
          <p className="mt-1 text-sm text-stone-600">
            Generate shareable survey links for a specific degree. The label
            is how you'll spot the outreach batch later (e.g. "Cambridge NatSci
            2026 grads Slack").
          </p>
        </div>
        <Link
          to="/admin/reviews"
          data-testid="admin-queue-link"
          className="text-xs font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
        >
          Moderation queue →
        </Link>
      </div>

      <form onSubmit={create}
            data-testid="admin-invite-form"
            className="mt-8 p-4 border border-stone-200 rounded-lg bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end">
          <div>
            <Label htmlFor="inv-degree" className="label-uppercase">Degree ID</Label>
            <Input
              id="inv-degree"
              data-testid="admin-invite-degree"
              className="mt-1 font-mono text-sm"
              value={form.degree_id}
              onChange={(e) => setForm((s) => ({ ...s, degree_id: e.target.value }))}
              placeholder="e.g. uk-oxford__ppe__oxford__2027-10"
            />
          </div>
          <div>
            <Label htmlFor="inv-label" className="label-uppercase">Label (optional)</Label>
            <Input
              id="inv-label"
              data-testid="admin-invite-label"
              className="mt-1 text-sm"
              value={form.label}
              onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
              placeholder="Cambridge NatSci Slack, June 2026"
            />
          </div>
          <Button
            type="submit"
            disabled={creating}
            data-testid="admin-invite-create"
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white h-10"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" /> Create link
              </>
            )}
          </Button>
        </div>
      </form>

      {state.items.length === 0 && (
        <div
          data-testid="admin-invites-empty"
          className="mt-8 p-8 border border-dashed border-stone-300 rounded-lg text-stone-500 text-sm"
        >
          No invites yet. Generate one above and share the link via email,
          Slack, alumni networks, etc.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {state.items.map((inv) => {
          const shareUrl = `${window.location.origin}/survey/${inv.degree_id}?t=${inv.token}`;
          return (
            <div
              key={inv.token}
              data-testid={`admin-invite-${inv.token}`}
              className="p-4 border border-stone-200 rounded-lg bg-white"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/degree/${inv.degree_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#4C1D95] hover:underline inline-flex items-center gap-1 font-mono truncate"
                    >
                      {inv.degree_id}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                    {inv.label && (
                      <span className="text-xs font-underground-sc tracking-wider bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">
                        {inv.label}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs font-mono text-stone-500 break-all">
                    {shareUrl}
                  </div>
                  <div className="mt-2 flex gap-4 text-[11px] font-underground-sc tracking-wider text-stone-500">
                    <span>Clicks <strong className="text-stone-800">{inv.click_count || 0}</strong></span>
                    <span>Submissions <strong className="text-stone-800">{inv.submission_count || 0}</strong></span>
                    <span>Created {new Date(inv.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(inv.token, inv.degree_id)}
                    data-testid={`admin-invite-copy-${inv.token}`}
                  >
                    {copiedTok === inv.token ? (
                      <><Check className="h-3.5 w-3.5 mr-1" /> Copied</>
                    ) : (
                      <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => destroy(inv.token)}
                    data-testid={`admin-invite-delete-${inv.token}`}
                    className="text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
